import { useState } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Mail, ArrowLeft } from "lucide-react";
import { Input } from "../components/ui/Input.jsx";
import { Button } from "../components/ui/Button.jsx";
import * as authApi from "../api/auth.api.js";

const schema = z.object({
  email: z.string().email("Invalid email"),
});

export default function ForgotPasswordPage() {
  const [submitted, setSubmitted] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(schema) });

  const onSubmit = async (data) => {
    try {
      await authApi.forgotPassword(data);
    } finally {
      // Always show the success state — the backend deliberately doesn't
      // reveal whether the email exists.
      setSubmitted(true);
    }
  };

  return (
    <main className="flex min-h-screen items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-accent text-bg-base">
            <Mail className="h-5 w-5" />
          </div>
          <h1 className="font-display text-3xl text-text-primary">Reset password</h1>
        </div>

        {submitted ? (
          <div className="rounded-xl border border-border-subtle bg-bg-elevated p-6 text-center shadow-card">
            <p className="text-sm text-text-primary">
              If that email is registered, a reset link is on its way.
            </p>
            <p className="mt-2 text-xs text-text-muted">
              Check your inbox (and spam folder). The link is valid for one hour.
            </p>
            <Link
              to="/login"
              className="mt-6 inline-flex items-center gap-1 text-xs text-accent hover:text-accent-hover"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to sign in
            </Link>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-5 rounded-xl border border-border-subtle bg-bg-elevated p-6 shadow-card"
            noValidate
          >
            <Input
              label="Email"
              type="email"
              autoComplete="email"
              autoFocus
              required
              error={errors.email?.message}
              hint="We'll email a reset link to this address."
              {...register("email")}
            />
            <Button type="submit" className="w-full" loading={isSubmitting}>
              Send reset link
            </Button>
            <div className="text-center text-xs">
              <Link to="/login" className="text-text-secondary hover:text-accent">
                Back to sign in
              </Link>
            </div>
          </form>
        )}
      </div>
    </main>
  );
}
